import React, { useEffect, useState } from 'react';
import axios from 'axios';
import api from '../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Edit, Trash2, Building2, Phone, Mail, Search, Package, Globe, FileText, MapPin, UserPlus, X, Factory, Warehouse, Paperclip, Truck } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { HelpTooltip, TitleWithHelp } from '../components/ui/help-tooltip';
import AnexosManager from '../components/AnexosManager';
import AnexosErrorBoundary from '../components/AnexosErrorBoundary';
import { ViewToggle } from '../components/ui/view-toggle';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import MoedaSelect from '../components/compras/MoedaSelect';
import { PAISES, INCOTERMS, TIPO_FORNECEDOR, isBrasil, getPaisNome } from '../lib/tradeConstants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('nautilus_token')}` }
});

// WhatsApp icon component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const Fornecedores = () => {
  const [fornecedores, setFornecedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingFornecedor, setEditingFornecedor] = useState(null);
  const [deleteFornecedorId, setDeleteFornecedorId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('dados');
  const [viewMode, setViewMode] = useState('grid');
  
  const emptyForm = {
    razao_social: '',
    nome_fantasia: '',
    pais: 'BR',
    cnpj: '',
    tax_id: '',
    inscricao_estadual: '',
    inscricao_municipal: '',
    endereco: '',
    cidade: '',
    estado: '',
    cep: '',
    contato_nome: '',
    telefone: '',
    whatsapp: '',
    email: '',
    site: '',
    catalogo_url: '',
    contatos: [],
    observacoes: '',
    anexos: [],
    // Defaults comerciais / importação
    moeda_padrao: 'BRL',
    incoterm_padrao: '',
    lead_time_padrao_dias: '',
    tipo_fornecedor: '',
    porto_origem_habitual: '',
    ncm_hs_principais: '',
    // Novos campos para integração com Fábrica
    is_fabrica: false,
    fabrica_criar_local_estoque: false
  };

  const emptyFabricaForm = {
    name: '',
    location: '',
    manager: '',
    capacity: '',
    criar_local_estoque: true
  };

  const [formData, setFormData] = useState(emptyForm);
  const [showFabricaDialog, setShowFabricaDialog] = useState(false);
  const [fabricaFormData, setFabricaFormData] = useState(emptyFabricaForm);
  const [pendingFornecedorId, setPendingFornecedorId] = useState(null);

  useEffect(() => {
    document.title = "Fornecedores — NautilusOS";
    fetchFornecedores();
    
    const timeout = setTimeout(() => {
      if (loading) setLoading(false);
    }, 5000);
    
    return () => clearTimeout(timeout);
  }, []);

  const fetchFornecedores = async () => {
    try {
      const response = await api.get('/fornecedores');
      setFornecedores(response.data);
    } catch (error) {
      toast.error('Erro ao carregar fornecedores');
      console.error('Error loading fornecedores:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (fornecedor) => {
    setEditingFornecedor(fornecedor);
    setFormData({
      razao_social: fornecedor.razao_social || fornecedor.nome || '',
      nome_fantasia: fornecedor.nome_fantasia || '',
      pais: fornecedor.pais || 'BR',
      cnpj: fornecedor.cnpj || '',
      tax_id: fornecedor.tax_id || '',
      inscricao_estadual: fornecedor.inscricao_estadual || '',
      inscricao_municipal: fornecedor.inscricao_municipal || '',
      endereco: fornecedor.endereco || '',
      cidade: fornecedor.cidade || '',
      estado: fornecedor.estado || '',
      cep: fornecedor.cep || '',
      contato_nome: fornecedor.contato_nome || '',
      telefone: fornecedor.telefone || '',
      whatsapp: fornecedor.whatsapp || '',
      email: fornecedor.email || '',
      site: fornecedor.site || '',
      catalogo_url: fornecedor.catalogo_url || '',
      contatos: fornecedor.contatos || [],
      observacoes: fornecedor.observacoes || '',
      anexos: fornecedor.anexos || [],
      moeda_padrao: fornecedor.moeda_padrao || 'BRL',
      incoterm_padrao: fornecedor.incoterm_padrao || '',
      lead_time_padrao_dias: fornecedor.lead_time_padrao_dias ?? '',
      tipo_fornecedor: fornecedor.tipo_fornecedor || '',
      porto_origem_habitual: fornecedor.porto_origem_habitual || '',
      ncm_hs_principais: fornecedor.ncm_hs_principais || '',
      is_fabrica: fornecedor.is_fabrica || false,
      fabrica_id: fornecedor.fabrica_id || null,
      fabrica_criar_local_estoque: false
    });
    setActiveTab('dados');
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (submitLoading) return;
    if (!formData.razao_social.trim()) {
      toast.error('Razão Social é obrigatória');
      return;
    }
    
    // Validar contatos - nome é obrigatório se tiver contato
    for (let i = 0; i < formData.contatos.length; i++) {
      const contato = formData.contatos[i];
      if (!contato.nome || !contato.nome.trim()) {
        toast.error(`O nome do Contato ${i + 1} é obrigatório`);
        setActiveTab('contato');
        return;
      }
    }
    
    setSubmitLoading(true);
    try {
      let fornecedorData = { ...formData };
      
      // Se marcou como fábrica e não é edição com fábrica já existente
      if (formData.is_fabrica && !formData.fabrica_id) {
        // Abrir dialog para preencher dados da fábrica
        setSubmitLoading(false);
        setShowDialog(false);
        setFabricaFormData({
          name: formData.nome_fantasia || formData.razao_social,
          location: `${formData.endereco}${formData.cidade ? ', ' + formData.cidade : ''}${formData.estado ? ' - ' + formData.estado : ''}`,
          manager: formData.contatos?.[0]?.nome || '',
          capacity: '',
          criar_local_estoque: formData.fabrica_criar_local_estoque
        });
        setPendingFornecedorId(editingFornecedor?.id || null);
        setShowFabricaDialog(true);
        return;
      }
      
      if (editingFornecedor) {
        const response = await api.put(`/fornecedores/${editingFornecedor.id}`, fornecedorData);
        toast.success('Fornecedor atualizado com sucesso!');
        setFornecedores(fornecedores.map(f => 
          f.id === editingFornecedor.id ? response.data : f
        ));
      } else {
        const response = await api.post('/fornecedores', fornecedorData);
        toast.success('Fornecedor criado com sucesso!');
        setFornecedores([...fornecedores, response.data]);
      }
      
      handleCloseDialog();
    } catch (error) {
      toast.error(editingFornecedor ? 'Erro ao atualizar fornecedor' : 'Erro ao criar fornecedor');
      console.error('Error saving fornecedor:', error);
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handler para criar fábrica junto com fornecedor
  const handleCreateFabricaAndFornecedor = async (e) => {
    e.preventDefault();
    
    if (!fabricaFormData.name || !fabricaFormData.capacity) {
      toast.error('Nome e Capacidade da fábrica são obrigatórios');
      return;
    }
    
    setSubmitLoading(true);
    try {
      // 1. Criar a fábrica primeiro
      const fabricaPayload = {
        name: fabricaFormData.name,
        location: fabricaFormData.location,
        manager: fabricaFormData.manager,
        capacity: parseInt(fabricaFormData.capacity),
        criar_local_estoque: fabricaFormData.criar_local_estoque
      };
      
      const fabricaResponse = await api.post('/factories', fabricaPayload);
      const fabricaCriada = fabricaResponse.data;
      
      // 2. Criar ou atualizar o fornecedor com vínculo da fábrica
      const fornecedorData = {
        ...formData,
        is_fabrica: true,
        fabrica_id: fabricaCriada.id
      };
      
      let fornecedorResponse;
      if (pendingFornecedorId) {
        fornecedorResponse = await api.put(`/fornecedores/${pendingFornecedorId}`, fornecedorData);
        setFornecedores(fornecedores.map(f => 
          f.id === pendingFornecedorId ? fornecedorResponse.data : f
        ));
      } else {
        fornecedorResponse = await api.post('/fornecedores', fornecedorData);
        setFornecedores([...fornecedores, fornecedorResponse.data]);
      }
      
      toast.success('Fornecedor e Fábrica criados com sucesso!');
      if (fabricaFormData.criar_local_estoque) {
        toast.success('Local de estoque criado automaticamente!');
      }
      
      // Limpar estados
      setShowFabricaDialog(false);
      setFabricaFormData(emptyFabricaForm);
      setPendingFornecedorId(null);
      handleCloseDialog();
      
    } catch (error) {
      toast.error('Erro ao criar fornecedor/fábrica');
      console.error('Error:', error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/fornecedores/${deleteFornecedorId}`);
      toast.success('Fornecedor excluído com sucesso!');
      setFornecedores(fornecedores.filter(f => f.id !== deleteFornecedorId));
      setDeleteFornecedorId(null);
    } catch (error) {
      toast.error('Erro ao excluir fornecedor');
    }
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingFornecedor(null);
    setFormData(emptyForm);
    setActiveTab('dados');
  };

  // Cancelar criação de fábrica
  const handleCancelFabrica = () => {
    setShowFabricaDialog(false);
    setFabricaFormData(emptyFabricaForm);
    setPendingFornecedorId(null);
    // Reabrir dialog do fornecedor para continuar edição
    setShowDialog(true);
  };

  // Adicionar contato
  const addContato = () => {
    setFormData({
      ...formData,
      contatos: [...formData.contatos, { nome: '', cargo: '', telefone: '', whatsapp: '', email: '' }]
    });
  };

  // Remover contato
  const removeContato = (index) => {
    setFormData({
      ...formData,
      contatos: formData.contatos.filter((_, i) => i !== index)
    });
  };

  // Atualizar contato
  const updateContato = (index, field, value) => {
    const updated = [...formData.contatos];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, contatos: updated });
  };

  // Formatar WhatsApp para link
  const formatWhatsAppLink = (number) => {
    const clean = number.replace(/\D/g, '');
    const withCountry = clean.startsWith('55') ? clean : `55${clean}`;
    return `https://wa.me/${withCountry}`;
  };

  const filteredFornecedores = fornecedores.filter(f => {
    const searchLower = searchTerm.toLowerCase();
    return (f.razao_social || f.nome || '').toLowerCase().includes(searchLower) ||
           (f.nome_fantasia || '').toLowerCase().includes(searchLower) ||
           (f.cnpj || '').includes(searchTerm) ||
           (f.tax_id || '').toLowerCase().includes(searchLower) ||
           (f.email || '').toLowerCase().includes(searchLower);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="fornecedores-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <TitleWithHelp
            title="Fornecedores"
            description="Cadastro de fornecedores de materiais e serviços. Inclui dados fiscais, contatos e link direto para WhatsApp. Ideal para agilizar cotações e comunicação."
            connections={['Matéria-Prima']}
          >
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Truck className="h-6 w-6 text-blue-600" />
              Fornecedores
            </h1>
          </TitleWithHelp>
          <p className="text-gray-500 mt-1">Cadastro completo de fornecedores</p>
        </div>
        <div className="flex items-center gap-3">
          <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
          <Button onClick={() => setShowDialog(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="mr-2 h-4 w-4" />
            Novo Fornecedor
          </Button>
        </div>
      </div>

      {/* Busca */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
        <Input
          placeholder="Buscar por razão social, nome fantasia, CNPJ ou email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  Total
                  <HelpTooltip 
                    title="Total de Fornecedores"
                    description="Quantidade total de fornecedores cadastrados no sistema."
                    iconSize={12}
                  />
                </p>
                <p className="text-2xl font-bold">{fornecedores.length}</p>
              </div>
              <Building2 className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  Com WhatsApp
                  <HelpTooltip 
                    title="Fornecedores com WhatsApp"
                    description="Fornecedores com número de WhatsApp cadastrado. Clique no cartão para iniciar conversa direta."
                    iconSize={12}
                  />
                </p>
                <p className="text-2xl font-bold">{fornecedores.filter(f => f.whatsapp).length}</p>
              </div>
              <WhatsAppIcon className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  Com Catálogo
                  <HelpTooltip 
                    title="Fornecedores com Catálogo"
                    description="Fornecedores que possuem catálogo ou anexos cadastrados."
                    iconSize={12}
                  />
                </p>
                <p className="text-2xl font-bold">{fornecedores.filter(f => f.catalogo_url || f.anexos?.length > 0).length}</p>
              </div>
              <FileText className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista */}
      {filteredFornecedores.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'Nenhum fornecedor encontrado' : 'Nenhum fornecedor cadastrado'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm ? 'Tente ajustar a busca' : 'Comece cadastrando seu primeiro fornecedor'}
            </p>
            {!searchTerm && (
              <Button onClick={() => setShowDialog(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Cadastrar Fornecedor
              </Button>
            )}
          </CardContent>
        </Card>
      ) : viewMode === 'list' ? (
        <Card data-testid="fornecedores-list-view">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50/80">
                  <th className="text-left p-3 font-medium text-gray-600">Razão Social</th>
                  <th className="text-left p-3 font-medium text-gray-600">Nome Fantasia</th>
                  <th className="text-left p-3 font-medium text-gray-600">Cidade/UF</th>
                  <th className="text-left p-3 font-medium text-gray-600">Telefone</th>
                  <th className="text-left p-3 font-medium text-gray-600">Email</th>
                  <th className="text-left p-3 font-medium text-gray-600">WhatsApp</th>
                  <th className="text-left p-3 font-medium text-gray-600">Tipo</th>
                  <th className="text-right p-3 font-medium text-gray-600">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredFornecedores.map((fornecedor) => (
                  <tr key={fornecedor.id} className="border-b hover:bg-blue-50/40 transition-colors group" data-testid={`fornecedor-row-${fornecedor.id}`}>
                    <td className="p-3">
                      <span className="font-medium text-gray-900">{fornecedor.razao_social || fornecedor.nome}</span>
                      {fornecedor.cnpj && <p className="text-xs text-gray-400">{fornecedor.cnpj}</p>}
                      {!fornecedor.cnpj && fornecedor.tax_id && <p className="text-xs text-gray-400">{fornecedor.tax_id}</p>}
                      {fornecedor.pais && !isBrasil(fornecedor.pais) && (
                        <span className="inline-flex items-center gap-1 text-[10px] mt-0.5 px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200" data-testid={`fornecedor-pais-tag-${fornecedor.id}`}>
                          <Globe className="h-2.5 w-2.5" /> {getPaisNome(fornecedor.pais)}
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-gray-600">{fornecedor.nome_fantasia || '—'}</td>
                    <td className="p-3 text-gray-600">{[fornecedor.cidade, fornecedor.estado].filter(Boolean).join(' - ') || '—'}</td>
                    <td className="p-3 text-gray-600">{fornecedor.telefone || '—'}</td>
                    <td className="p-3 text-gray-600 max-w-[200px] truncate">{fornecedor.email || '—'}</td>
                    <td className="p-3">
                      {fornecedor.whatsapp ? (
                        <a href={formatWhatsAppLink(fornecedor.whatsapp)} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-700 flex items-center gap-1">
                          <WhatsAppIcon className="h-4 w-4" />
                          {fornecedor.whatsapp}
                        </a>
                      ) : '—'}
                    </td>
                    <td className="p-3">
                      {fornecedor.is_fabrica && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                          <Factory className="h-3 w-3" />
                          Fábrica
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleEdit(fornecedor)} title="Editar">
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => setDeleteFornecedorId(fornecedor.id)} title="Excluir">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredFornecedores.map((fornecedor) => (
            <Card key={fornecedor.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-lg truncate">
                        {fornecedor.razao_social || fornecedor.nome}
                      </CardTitle>
                      {fornecedor.is_fabrica && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                          <Factory className="h-3 w-3" />
                          Fábrica
                        </span>
                      )}
                    </div>
                    {fornecedor.nome_fantasia && (
                      <p className="text-sm text-gray-500 truncate">{fornecedor.nome_fantasia}</p>
                    )}
                    {fornecedor.cnpj && (
                      <p className="text-xs text-gray-400 mt-1">CNPJ: {fornecedor.cnpj}</p>
                    )}
                    {!fornecedor.cnpj && fornecedor.tax_id && (
                      <p className="text-xs text-gray-400 mt-1">Tax ID/VAT: {fornecedor.tax_id}</p>
                    )}
                    {fornecedor.pais && !isBrasil(fornecedor.pais) && (
                      <span className="inline-flex items-center gap-1 text-[10px] mt-1 px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                        <Globe className="h-2.5 w-2.5" /> {getPaisNome(fornecedor.pais)}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-1 ml-2">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(fornecedor)} className="h-8 w-8 p-0">
                      <Edit className="h-4 w-4 text-gray-500" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteFornecedorId(fornecedor.id)} className="h-8 w-8 p-0">
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {/* WhatsApp destacado */}
                {fornecedor.whatsapp && (
                  <a 
                    href={formatWhatsAppLink(fornecedor.whatsapp)} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-green-50 hover:bg-green-100 text-green-700 px-3 py-2 rounded-lg transition-colors"
                  >
                    <WhatsAppIcon className="h-5 w-5 text-green-600" />
                    <span className="font-medium">{fornecedor.whatsapp}</span>
                  </a>
                )}
                
                {(fornecedor.cidade || fornecedor.estado) && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{[fornecedor.cidade, fornecedor.estado].filter(Boolean).join(' - ')}</span>
                  </div>
                )}
                {fornecedor.telefone && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Phone className="h-4 w-4 flex-shrink-0" />
                    <span>{fornecedor.telefone}</span>
                  </div>
                )}
                {fornecedor.email && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{fornecedor.email}</span>
                  </div>
                )}
                {fornecedor.site && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Globe className="h-4 w-4 flex-shrink-0" />
                    <a href={fornecedor.site.startsWith('http') ? fornecedor.site : `https://${fornecedor.site}`} 
                       target="_blank" rel="noopener noreferrer" 
                       className="truncate text-blue-600 hover:underline">
                      {fornecedor.site}
                    </a>
                  </div>
                )}
                {fornecedor.catalogo_url && (
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 flex-shrink-0 text-purple-600" />
                    <a href={fornecedor.catalogo_url} target="_blank" rel="noopener noreferrer" 
                       className="text-purple-600 hover:underline text-sm">
                      Ver Catálogo
                    </a>
                  </div>
                )}
                {fornecedor.observacoes && (
                  <p className="text-gray-500 text-xs mt-2 line-clamp-2 border-t pt-2">
                    {fornecedor.observacoes}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal de Criação/Edição */}
      <Dialog open={showDialog} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingFornecedor ? 'Editar Fornecedor' : 'Novo Fornecedor'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit}>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="dados">Dados</TabsTrigger>
                <TabsTrigger value="contato">Contato</TabsTrigger>
                <TabsTrigger value="anexos" className="flex items-center gap-1">
                  <Paperclip className="h-3 w-3" />
                  Anexos
                </TabsTrigger>
                <TabsTrigger value="adicional">Adicional</TabsTrigger>
              </TabsList>

              {/* Aba Dados Cadastrais */}
              <TabsContent value="dados" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label>Razão Social *</Label>
                    <Input
                      value={formData.razao_social}
                      onChange={(e) => setFormData({ ...formData, razao_social: e.target.value })}
                      placeholder="Nome empresarial oficial"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nome Fantasia</Label>
                    <Input
                      value={formData.nome_fantasia}
                      onChange={(e) => setFormData({ ...formData, nome_fantasia: e.target.value })}
                      placeholder="Nome comercial"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>País</Label>
                    <Select
                      value={formData.pais || 'BR'}
                      onValueChange={(v) => {
                        const paisMeta = PAISES.find(p => p.code === v);
                        setFormData(prev => ({
                          ...prev,
                          pais: v,
                          // Sugere a moeda padrão do país se ainda estiver no default
                          moeda_padrao: (!prev.moeda_padrao || prev.moeda_padrao === 'BRL') && paisMeta?.moeda ? paisMeta.moeda : prev.moeda_padrao,
                        }));
                      }}
                    >
                      <SelectTrigger data-testid="fornecedor-pais">
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-muted-foreground" />
                          <SelectValue placeholder="País" />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {PAISES.map(p => (
                          <SelectItem key={p.code} value={p.code} data-testid={`pais-opt-${p.code}`}>{p.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{isBrasil(formData.pais) ? 'CNPJ' : 'Tax ID / VAT'}</Label>
                    {isBrasil(formData.pais) ? (
                      <Input
                        value={formData.cnpj}
                        onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                        placeholder="00.000.000/0000-00"
                        data-testid="fornecedor-cnpj"
                      />
                    ) : (
                      <Input
                        value={formData.tax_id}
                        onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                        placeholder="Ex: VAT / EIN / Tax ID"
                        data-testid="fornecedor-tax-id"
                      />
                    )}
                  </div>
                  {isBrasil(formData.pais) && (
                    <>
                      <div className="space-y-2">
                        <Label>Inscrição Estadual</Label>
                        <Input
                          value={formData.inscricao_estadual}
                          onChange={(e) => setFormData({ ...formData, inscricao_estadual: e.target.value })}
                          placeholder="Inscrição estadual"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Inscrição Municipal</Label>
                        <Input
                          value={formData.inscricao_municipal}
                          onChange={(e) => setFormData({ ...formData, inscricao_municipal: e.target.value })}
                          placeholder="Inscrição municipal"
                        />
                      </div>
                    </>
                  )}
                </div>

                {/* Condições comerciais padrão (pré-preenchem cotações/pedidos) */}
                <div className="border-t pt-4 mt-2">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Truck className="h-4 w-4 text-slate-500" />
                    Condições comerciais padrão
                    <HelpTooltip content="Usadas para pré-preencher automaticamente as cotações e pedidos deste fornecedor. Você sempre pode ajustar em cada negociação." />
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Moeda padrão</Label>
                      <MoedaSelect
                        value={formData.moeda_padrao || 'BRL'}
                        onChange={(v) => setFormData({ ...formData, moeda_padrao: v })}
                        className="w-full"
                        testId="fornecedor-moeda-padrao"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Incoterm padrão</Label>
                      <Select
                        value={formData.incoterm_padrao || 'none'}
                        onValueChange={(v) => setFormData({ ...formData, incoterm_padrao: v === 'none' ? '' : v })}
                      >
                        <SelectTrigger data-testid="fornecedor-incoterm-padrao">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Nenhum —</SelectItem>
                          {INCOTERMS.map(i => (
                            <SelectItem key={i.code} value={i.code}>{i.nome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Lead Time padrão (dias)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.lead_time_padrao_dias}
                        onChange={(e) => setFormData({ ...formData, lead_time_padrao_dias: e.target.value === '' ? '' : parseInt(e.target.value) })}
                        placeholder="Ex: 45"
                        data-testid="fornecedor-lead-time"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Tipo</Label>
                      <Select
                        value={formData.tipo_fornecedor || 'none'}
                        onValueChange={(v) => setFormData({ ...formData, tipo_fornecedor: v === 'none' ? '' : v })}
                      >
                        <SelectTrigger data-testid="fornecedor-tipo">
                          <SelectValue placeholder="Fabricante / Distribuidor" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Não informado —</SelectItem>
                          {TIPO_FORNECEDOR.map(t => (
                            <SelectItem key={t.code} value={t.code}>{t.nome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {!isBrasil(formData.pais) && (
                      <div className="space-y-2">
                        <Label>Porto/Aeroporto de origem habitual</Label>
                        <Input
                          value={formData.porto_origem_habitual}
                          onChange={(e) => setFormData({ ...formData, porto_origem_habitual: e.target.value })}
                          placeholder="Ex: Shanghai (CNSHA)"
                          data-testid="fornecedor-porto-origem"
                        />
                      </div>
                    )}
                    <div className="space-y-2 col-span-2">
                      <Label>NCM/HS Code dos principais produtos</Label>
                      <Input
                        value={formData.ncm_hs_principais}
                        onChange={(e) => setFormData({ ...formData, ncm_hs_principais: e.target.value })}
                        placeholder="Ex: 8407.21, 8409.99 (separe por vírgula)"
                        data-testid="fornecedor-ncm-hs"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Endereço</Label>
                  <Input
                    value={formData.endereco}
                    onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                    placeholder="Rua, número, complemento"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Cidade</Label>
                    <Input
                      value={formData.cidade}
                      onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                      placeholder="Cidade"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Estado</Label>
                    <Input
                      value={formData.estado}
                      onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                      placeholder="UF"
                      maxLength={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>CEP</Label>
                    <Input
                      value={formData.cep}
                      onChange={(e) => setFormData({ ...formData, cep: e.target.value })}
                      placeholder="00000-000"
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Aba Contato */}
              <TabsContent value="contato" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label>Nome do Contato Principal</Label>
                    <Input
                      value={formData.contato_nome}
                      onChange={(e) => setFormData({ ...formData, contato_nome: e.target.value })}
                      placeholder="Nome da pessoa de contato"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Telefone Principal</Label>
                    <Input
                      value={formData.telefone}
                      onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                      placeholder="(00) 0000-0000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>WhatsApp Principal</Label>
                    <Input
                      value={formData.whatsapp}
                      onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                      placeholder="(00) 00000-0000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="email@empresa.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Site</Label>
                    <Input
                      value={formData.site}
                      onChange={(e) => setFormData({ ...formData, site: e.target.value })}
                      placeholder="www.empresa.com.br"
                    />
                  </div>
                </div>

                {/* Contatos Adicionais */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-base font-medium">Contatos Adicionais</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addContato}>
                      <UserPlus className="h-4 w-4 mr-1" />
                      Adicionar
                    </Button>
                  </div>
                  
                  {formData.contatos.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">
                      Nenhum contato adicional cadastrado
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {formData.contatos.map((contato, index) => (
                        <Card key={contato.nome || contato.email || index} className="p-3">
                          <div className="flex items-start justify-between mb-2">
                            <span className="text-sm font-medium text-gray-600">Contato {index + 1}</span>
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeContato(index)} className="h-6 w-6 p-0">
                              <X className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              value={contato.nome}
                              onChange={(e) => updateContato(index, 'nome', e.target.value)}
                              placeholder="Nome *"
                              className={`h-8 text-sm ${!contato.nome ? 'border-orange-300' : ''}`}
                              required
                            />
                            <Input
                              value={contato.cargo}
                              onChange={(e) => updateContato(index, 'cargo', e.target.value)}
                              placeholder="Cargo"
                              className="h-8 text-sm"
                            />
                            <Input
                              value={contato.telefone}
                              onChange={(e) => updateContato(index, 'telefone', e.target.value)}
                              placeholder="Telefone"
                              className="h-8 text-sm"
                            />
                            <Input
                              value={contato.whatsapp}
                              onChange={(e) => updateContato(index, 'whatsapp', e.target.value)}
                              placeholder="WhatsApp"
                              className="h-8 text-sm"
                            />
                            <Input
                              value={contato.email}
                              onChange={(e) => updateContato(index, 'email', e.target.value)}
                              placeholder="Email"
                              className="col-span-2 h-8 text-sm"
                            />
                          </div>
                          {!contato.nome && (
                            <p className="text-xs text-orange-600 mt-1">* Nome é obrigatório</p>
                          )}
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Aba Anexos */}
              <TabsContent value="anexos" className="space-y-4 mt-4">
                <AnexosErrorBoundary>
                  <AnexosManager
                    anexos={formData.anexos || []}
                    onChange={(novosAnexos) => setFormData({ ...formData, anexos: novosAnexos })}
                    maxFiles={15}
                    maxSizeMB={50}
                    label="Documentos do Fornecedor"
                    modulo="fornecedores"
                    entidadeId={editingFornecedor?.id}
                    categoria="documentacao"
                  />
                </AnexosErrorBoundary>
                <p className="text-xs text-gray-500">
                  Adicione catálogos, certificados, contratos, tabelas de preços, etc.
                </p>
              </TabsContent>

              {/* Aba Adicional */}
              <TabsContent value="adicional" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Link para Catálogo de Produtos</Label>
                  <Input
                    value={formData.catalogo_url}
                    onChange={(e) => setFormData({ ...formData, catalogo_url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Observações</Label>
                  <Textarea
                    value={formData.observacoes}
                    onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                    placeholder="Informações adicionais sobre o fornecedor..."
                    rows={4}
                  />
                </div>

                {/* Seção: Fornecedor é também Fábrica */}
                <div className="border-t pt-4">
                  <div className="p-4 border rounded-lg bg-purple-50 space-y-3">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="is_fabrica"
                        checked={formData.is_fabrica}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          is_fabrica: e.target.checked,
                          fabrica_criar_local_estoque: e.target.checked 
                        })}
                        className="w-5 h-5 rounded border-gray-300 mt-0.5"
                        disabled={formData.fabrica_id}
                      />
                      <div className="flex-1">
                        <Label htmlFor="is_fabrica" className="cursor-pointer font-medium flex items-center gap-2">
                          <Factory className="h-4 w-4 text-purple-600" />
                          Este fornecedor também é uma Fábrica
                        </Label>
                        <p className="text-xs text-gray-600 mt-1">
                          Ao marcar esta opção, o fornecedor será incluído automaticamente no cadastro de Fábricas
                        </p>
                      </div>
                    </div>
                    
                    {formData.is_fabrica && !formData.fabrica_id && (
                      <div className="ml-8 p-3 bg-white rounded border space-y-2">
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            id="fabrica_criar_local_estoque"
                            checked={formData.fabrica_criar_local_estoque}
                            onChange={(e) => setFormData({ 
                              ...formData, 
                              fabrica_criar_local_estoque: e.target.checked 
                            })}
                            className="w-4 h-4 rounded border-gray-300 mt-0.5"
                          />
                          <div>
                            <Label htmlFor="fabrica_criar_local_estoque" className="cursor-pointer text-sm flex items-center gap-2">
                              <Warehouse className="h-4 w-4 text-blue-600" />
                              Criar local de estoque para esta fábrica
                            </Label>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Um local de estoque será criado automaticamente vinculado à fábrica
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {formData.fabrica_id && (
                      <div className="ml-8 p-2 bg-green-100 rounded text-sm text-green-800 flex items-center gap-2">
                        <Factory className="h-4 w-4" />
                        Este fornecedor já está vinculado a uma fábrica
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitLoading} className="bg-blue-600 hover:bg-blue-700">
                {submitLoading ? 'Salvando...' : (editingFornecedor ? 'Salvar' : 'Criar')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de Exclusão */}
      <AlertDialog open={!!deleteFornecedorId} onOpenChange={() => setDeleteFornecedorId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este fornecedor? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog para completar dados da Fábrica */}
      <Dialog open={showFabricaDialog} onOpenChange={(open) => !open && handleCancelFabrica()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Factory className="h-5 w-5 text-purple-600" />
              Completar Cadastro de Fábrica
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateFabricaAndFornecedor} className="space-y-4">
            <p className="text-sm text-gray-600 bg-blue-50 p-3 rounded">
              O fornecedor <strong>{formData.nome_fantasia || formData.razao_social}</strong> será registrado também como uma fábrica. 
              Complete os dados abaixo:
            </p>
            
            <div className="space-y-2">
              <Label>Nome da Fábrica *</Label>
              <Input
                value={fabricaFormData.name}
                onChange={(e) => setFabricaFormData({ ...fabricaFormData, name: e.target.value })}
                placeholder="Nome da unidade fabril"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label>Localização</Label>
              <Input
                value={fabricaFormData.location}
                onChange={(e) => setFabricaFormData({ ...fabricaFormData, location: e.target.value })}
                placeholder="Endereço da fábrica"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Gerente Responsável</Label>
              <Input
                value={fabricaFormData.manager}
                onChange={(e) => setFabricaFormData({ ...fabricaFormData, manager: e.target.value })}
                placeholder="Nome do gerente"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Capacidade (unidades/mês) *</Label>
              <Input
                type="number"
                value={fabricaFormData.capacity}
                onChange={(e) => setFabricaFormData({ ...fabricaFormData, capacity: e.target.value })}
                placeholder="Ex: 10"
                required
              />
            </div>
            
            {/* Checkbox Local de Estoque */}
            <div className="flex items-center gap-3 p-3 border rounded-lg bg-blue-50">
              <input
                type="checkbox"
                id="fabrica_local_estoque"
                checked={fabricaFormData.criar_local_estoque}
                onChange={(e) => setFabricaFormData({ ...fabricaFormData, criar_local_estoque: e.target.checked })}
                className="w-5 h-5 rounded border-gray-300"
              />
              <div>
                <Label htmlFor="fabrica_local_estoque" className="cursor-pointer font-medium flex items-center gap-2">
                  <Warehouse className="h-4 w-4 text-blue-600" />
                  Criar Local de Estoque
                </Label>
                <p className="text-xs text-gray-600">
                  Criar automaticamente um local de estoque para esta fábrica
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCancelFabrica}>
                Voltar
              </Button>
              <Button type="submit" disabled={submitLoading} className="bg-purple-600 hover:bg-purple-700">
                {submitLoading ? 'Criando...' : 'Criar Fornecedor e Fábrica'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Fornecedores;
